import {
  SkyBadge,
  SkyButton,
  SkyCard,
  SkyDialog,
  SkyMessageBar,
  SkyProgressBar,
  SkyStepper,
  SkyText,
  SkyTextarea,
  type SkyStepItem,
} from "@skypointcloud/skypoint-ui";
import { useMemo, useState, type ReactNode } from "react";
import type { ProvisioningStep } from "@shared/types/instancesAdmin";
import { formatDuration } from "@/components/platform/format";
import {
  provisioningProgress,
  stepLabel,
  type ProvisioningProgress,
} from "@/components/platform/provisioningSteps";
import { ErrorState, GridSkeleton } from "@/components/platform/ScreenState";
import {
  PROVISIONING_POLL_INTERVAL_MS,
  isTerminalProvisioning,
  useProvisioningJob,
  useRetryInstanceProvisioning,
} from "@/hooks/use-instances-admin";
import { usePermissions } from "@/hooks/use-permissions";
import { ApiClientError, referenceIdOf } from "@/lib/api";
import { useNotice } from "@/lib/feedback/NoticeProvider";
import { CopyIcon, DismissIcon } from "@/lib/icons";
import { formatInstant } from "@/lib/time/displayTime";
import { useDisplayTz } from "@/hooks/use-display-tz";

/**
 * One instance's provisioning job, as a vertical stepper that polls (FR-20,
 * NFR-1, canvas 12 panels 01 and 03).
 *
 * Provisioning is a RUNNER job, not a request: creating an instance answers 202
 * with a job id and the work carries on in another process. So this panel is a
 * view of something already happening — closing it cancels nothing, and coming
 * back picks the job up again from the same route.
 *
 * The poll stops the moment the job is terminal. That decision lives in
 * `useProvisioningJob`, derived from the data itself rather than from a prop
 * the caller has to remember to flip, because the one time it was not flipped
 * the screen would keep asking for ever.
 */

function skippedWords(step: ProvisioningStep): string | null {
  if (step.status !== "skipped") return null;
  return step.sharedDevDatabase ? "skipped: shared dev database" : "skipped";
}

function stepperStatus(
  status: ProvisioningStep["status"],
): "completed" | "error" | "pending" | undefined {
  if (status === "succeeded" || status === "skipped") return "completed";
  if (status === "failed") return "error";
  if (status === "pending") return "pending";
  return undefined;
}

/** A recorded step, as SkyStepper wants it. */
function toStepItem(step: ProvisioningStep, labelOf: (name: string) => string): SkyStepItem {
  const duration = formatDuration(step.startedAt, step.finishedAt);
  // A bare "skipped" reads as a step that had nothing to do — a database that
  // happened to be there already. When `DATABASE_URL_TENANT_DEV` points every
  // instance at one shared database the step was skipped by an OVERRIDE, and
  // that is the one skip a reader must not mistake for routine, so it says so.
  const skipped = skippedWords(step);
  const description = [step.error ?? step.detail ?? null, skipped, duration]
    .filter((part): part is string => Boolean(part))
    .join(" · ");

  // `running` is deliberately NOT given a status: the library treats the step at
  // `activeStep` as active, and an explicit status would override it and freeze
  // the indicator on a step that is moving.
  const status = stepperStatus(step.status);

  return {
    label: labelOf(step.name),
    ...(description ? { description } : {}),
    ...(status ? { status } : {}),
  };
}

/**
 * The job the panel draws. An instance's job (`ProvisioningJobView`) fits it, and so does a
 * tenant's (`TenantProvisioningJob`, mapped by the Tenants drawer); the times are optional
 * because a tenant's job record carries none.
 */
export interface ProvisioningPanelJob {
  id: string;
  status: "queued" | "running" | "succeeded" | "failed";
  steps: ProvisioningStep[];
  error: string | null;
  startedAt?: string | null;
  createdAt?: string | null;
}

/** What the panel reads when the caller brings its own job (a tenant's) instead of an instance's. */
export interface ProvisioningPanelSource {
  data: ProvisioningPanelJob | undefined;
  isPending: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => unknown;
}

type JobStatus = ProvisioningPanelJob["status"];

function statusColor(status: JobStatus): "success" | "danger" | "informative" {
  if (status === "succeeded") return "success";
  if (status === "failed") return "danger";
  return "informative";
}

function statusWord(status: JobStatus): string {
  if (status === "succeeded") return "Succeeded";
  if (status === "failed") return "Failed";
  if (status === "running") return "Running";
  return "Queued";
}

function JobStatusBadge({ status }: { status: JobStatus }) {
  return (
    <SkyBadge appearance="tint" color={statusColor(status)} testId="provisioning-status">
      {statusWord(status)}
    </SkyBadge>
  );
}

function progressColor(failed: boolean, terminal: boolean): "error" | "success" | "brand" {
  if (failed) return "error";
  return terminal ? "success" : "brand";
}

/** The step line under the bar; it changes only when the step does. */
function stepLine(
  progress: ProvisioningProgress,
  failed: boolean,
  terminal: boolean,
  activeStepName: string | null,
): string {
  if (failed) return `Stopped at step ${progress.completed + 1} of ${progress.total}.`;
  if (terminal) return `All ${progress.total} steps finished.`;
  if (activeStepName) {
    return `Step ${progress.activeIndex + 1} of ${progress.total}: ${activeStepName.toLowerCase()}.`;
  }
  return `Queued. Refreshes every ${PROVISIONING_POLL_INTERVAL_MS / 1000} s.`;
}

/** Where the job stopped and why, with the reference id to quote. */
function ProvisioningFailure({
  failedStep,
  failedStepLabel,
  jobError,
  reference,
  override,
  retry,
}: {
  failedStep: ProvisioningStep | null;
  failedStepLabel: string | null;
  jobError: string | null | undefined;
  reference: string | null | undefined;
  override: { body: ReactNode; actions?: ReactNode } | undefined;
  /** F36: the instance job's Retry, for a holder of `platform.instances.manage`. */
  retry?: ReactNode;
}) {
  return (
    <SkyMessageBar
      intent="error"
      layout="multiline"
      title={`Provisioning stopped at ${failedStepLabel ?? "a step"}`}
      testId="provisioning-failure"
      message={
        override ? (
          override.body
        ) : (
          <span>
            {failedStep?.error ?? jobError}
            {reference ? (
              <>
                {" "}
                Reference id <span className="font-mono">{reference}</span>.
              </>
            ) : null}{" "}
            {retry
              ? "Fix the cause, then select Retry. The job runs again and passes over the steps already done."
              : "Fix the cause, then ask a platform admin to retry the job. The steps already done are not repeated blindly."}
          </span>
        )
      }
      actions={
        override ? (
          override.actions
        ) : retry || reference ? (
          <>
            {retry}
            {reference ? (
              <SkyButton
                appearance="subtle"
                size="small"
                icon={CopyIcon}
                onClick={() => {
                  void navigator.clipboard?.writeText(reference).catch(() => {
                    /* insecure origin — the id is on screen to be typed */
                  });
                }}
              >
                Copy reference
              </SkyButton>
            ) : null}
          </>
        ) : undefined
      }
    />
  );
}

/** F36: what the Retry confirm says when the instance is disabled (its Retry enables it too). */
export const RETRY_ENABLES_SENTENCE =
  "This instance is disabled. Retrying also enables it: the job runs again, and when it finishes the instance is active. Its schedules stay off until someone turns them on.";

/** F36: a disabled instance whose job finished without activating it (disabled while it ran). */
export const STRANDED_SENTENCE =
  "This instance was disabled before its provisioning finished, so it was never activated. Select Retry to finish it.";

export function ProvisioningPanel({
  instanceId,
  instanceName,
  onDismiss,
  source,
  stepLabels,
  title,
  headingLevel = "h2",
  failure,
  instance,
  testId = "provisioning-panel",
}: {
  instanceId: string | null;
  instanceName: string;
  onDismiss?: () => void;
  /** A job the caller reads itself (Tenants); absent, the panel polls the instance's job. */
  source?: ProvisioningPanelSource;
  /** Labels for step names the runner owns; a name with none falls back to `stepLabel`. */
  stepLabels?: Readonly<Record<string, string>>;
  /** Replaces "Provisioning <name>". */
  title?: string;
  headingLevel?: "h2" | "h3";
  /** Replaces the instance failure bar's body (and its actions) for a job that is not an instance's. */
  failure?: { body: ReactNode; actions?: ReactNode };
  /** F36: the instance's status and whether it was ever provisioned, for its Retry. */
  instance?: { status: string; provisionedAt: string | null };
  testId?: string;
}) {
  const zone = useDisplayTz();
  // A caller with its own source leaves the instance poll off (a null id disables the query).
  const ownJob = useProvisioningJob(source ? null : instanceId);
  const job: ProvisioningPanelSource = source ?? ownJob;
  const labelOf = (name: string) => stepLabels?.[name] ?? stepLabel(name);
  const progress = useMemo(() => provisioningProgress(job.data), [job.data]);
  const steps = (job.data?.steps ?? []).map((step) => toStepItem(step, labelOf));
  const terminal = isTerminalProvisioning(job.data?.status);
  const failed = job.data?.status === "failed";
  const reference = referenceIdOf(job.error);
  // F36: an instance's own job (no `source`) can be retried by a holder of the key the route needs.
  const { can } = usePermissions();
  const notice = useNotice();
  const retry = useRetryInstanceProvisioning();
  const [confirming, setConfirming] = useState(false);
  const [reason, setReason] = useState("");
  const canRetry = !source && instanceId !== null && can("platform.instances.manage");
  // A disabled instance's Retry enables it too, so it asks first and takes a reason. A stale list
  // that still reads enabled gets a 400 for the reason, which opens the same dialog.
  const reopening = instance?.status === "disabled";
  // The dead end the route also takes: the job succeeded but a disable kept it from activating.
  const stranded =
    job.data?.status === "succeeded" && reopening && instance?.provisionedAt === null;
  function retryJob(withReason?: string) {
    if (!instanceId) return;
    retry.mutate(
      { instanceId, reason: withReason },
      {
        onSuccess: () => {
          setConfirming(false);
          setReason("");
          notice.success("Retry queued");
        },
        // The server's own sentence says why (already running, nothing to retry, tenant disabled).
        onError: (error) => {
          if (error instanceof ApiClientError && error.status === 409) {
            setConfirming(false);
            notice.error(error.message);
          } else if (error instanceof ApiClientError && error.status === 400) {
            // A reason was wanted: the instance is disabled though the list read enabled (stale).
            // Open the "Retry and enable" dialog, where the server's sentence shows on the field.
            setConfirming(true);
          } else {
            setConfirming(false);
            notice.error("The retry was not queued. Try again.");
          }
        },
      },
    );
  }
  const retryButton = canRetry ? (
    <SkyButton
      appearance="primary"
      size="small"
      loading={retry.isPending && !confirming}
      onClick={() => (reopening ? setConfirming(true) : retryJob())}
      testId="provisioning-retry"
    >
      Retry
    </SkyButton>
  ) : undefined;
  const reasonError =
    retry.error instanceof ApiClientError && retry.error.status === 400
      ? (retry.error.details.find((detail) => detail.field === "reason")?.issue ??
        retry.error.message)
      : undefined;

  const activeStepName =
    progress.activeIndex >= 0 && job.data?.steps[progress.activeIndex]
      ? labelOf(job.data.steps[progress.activeIndex]!.name)
      : null;

  function renderJob(): ReactNode {
    if (job.isPending) {
      return <GridSkeleton rows={3} label="Reading the provisioning job…" />;
    }
    if (job.isError) {
      return (
        <ErrorState
          title="The provisioning job could not be read"
          error={job.error}
          onRetry={() => {
            job.refetch();
          }}
          testId="provisioning-error"
        />
      );
    }
    return (
      <>
        {/* The bar is a SUMMARY; the step list below it is the real content,
                which is why the bar carries a label and the list is not hidden
                behind it. */}
        <SkyProgressBar
          value={progress.percent}
          label={`Provisioning progress: ${progress.completed} of ${progress.total} steps`}
          color={progressColor(failed, terminal)}
          showValue
          testId="provisioning-progress"
        />

        {/* One polite announcement per step transition, not per poll: the
                text only changes when the step does. */}
        <SkyText size={200} color="muted" block aria-live="polite" testId="provisioning-step-line">
          {stepLine(progress, failed, terminal, activeStepName)}
        </SkyText>

        {failed && (failure || job.data?.error || progress.failedStep?.error) ? (
          <ProvisioningFailure
            failedStep={progress.failedStep}
            failedStepLabel={progress.failedStep ? labelOf(progress.failedStep.name) : null}
            jobError={job.data?.error}
            reference={reference}
            override={failure}
            retry={retryButton}
          />
        ) : null}

        {stranded && retryButton ? (
          <SkyMessageBar
            intent="warning"
            layout="multiline"
            title="Provisioning did not finish"
            message={STRANDED_SENTENCE}
            actions={retryButton}
            testId="provisioning-stranded"
          />
        ) : null}

        {steps.length > 0 ? (
          <SkyStepper
            steps={steps}
            activeStep={progress.activeIndex}
            orientation="vertical"
            size="medium"
            ariaLabel={title ? `${title}: progress steps` : undefined}
            testId="provisioning-stepper"
          />
        ) : (
          <SkyText size={200} color="muted" block>
            No step has started yet.
          </SkyText>
        )}
      </>
    );
  }

  return (
    <SkyCard appearance="outline" testId={testId}>
      <div className="flex flex-col gap-4 p-1">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <SkyText as={headingLevel} size={400} weight="semibold" block>
              {title ?? `Provisioning ${instanceName}`}
            </SkyText>
            {job.data ? (
              <SkyText size={200} color="muted" block>
                Job <span className="font-mono">{job.data.id}</span>
                {(job.data.startedAt ?? job.data.createdAt) ? (
                  <>
                    {" "}
                    · started{" "}
                    {formatInstant(job.data.startedAt ?? job.data.createdAt!, zone, "short", {
                      named: true,
                    })}
                  </>
                ) : null}
              </SkyText>
            ) : null}
          </div>
          <div className="flex items-center gap-2">
            {job.data ? <JobStatusBadge status={job.data.status} /> : null}
            {onDismiss ? (
              <SkyButton
                appearance="subtle"
                size="small"
                icon={DismissIcon}
                onClick={onDismiss}
                aria-label={`Close the provisioning panel for ${instanceName}`}
              >
                Close
              </SkyButton>
            ) : null}
          </div>
        </div>

        {renderJob()}
      </div>
      <SkyDialog
        open={confirming}
        onOpenChange={(open) => {
          setConfirming(open);
          if (!open) retry.reset();
        }}
        title={`Retry and enable ${instanceName}`}
        description={RETRY_ENABLES_SENTENCE}
        size="medium"
        testId="provisioning-retry-dialog"
        // Closes when the request succeeds, so a refused reason keeps what was typed.
        onBeforeClose={(closeReason) => closeReason !== "primaryAction"}
        primaryAction={{
          label: "Retry and enable",
          onClick: () => retryJob(reason.trim()),
          loading: retry.isPending,
        }}
        secondaryAction={{ label: "Cancel", onClick: () => setConfirming(false) }}
      >
        <SkyTextarea
          label="Reason"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          hint="Recorded on the audit event beside your name."
          validationMessage={reasonError}
          validationState={reasonError ? "error" : undefined}
          maxLength={500}
          rows={3}
          required
          testId="provisioning-retry-reason"
        />
      </SkyDialog>
    </SkyCard>
  );
}
